import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { Save, Edit, Delete, Add } from '@mui/icons-material';
import { leaveApi } from '../../../api/leaveApi';
import { LeavePeriodDto, UpdateLeavePeriod as UpdateLeavePeriodPayload } from '../../../types';
import { toast } from 'react-toastify';

// ─── Add Leave Period Dialog ──────────────────────────────────────────────────
const AddLeavePeriodDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, onClose, onSaved }) => {
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const resetForm = () => {
    setName('');
    setStartDate('');
    setEndDate('');
    setFormError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    setFormError(null);
    const normalizedName = name.trim();

    if (!normalizedName || !startDate || !endDate) {
      toast.error('Please fill all required fields');
      return;
    }

    setSubmitting(true);
    try {
      await leaveApi.createLeavePeriod({ name: normalizedName, startDate, endDate });
      toast.success('Leave period saved successfully!');
      resetForm();
      onSaved();
      onClose();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Failed to save leave period';
      setFormError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" sx={{ textTransform: 'none' }}>
      <DialogTitle>Add Leave Period</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Define a new leave cycle (e.g., Jan 2026 - Dec 2026).
        </Typography>

        {formError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={12}>
            <TextField
              label="Leave Period Name *"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => {
                const filteredValue = e.target.value.replace(/[^A-Za-z0-9\s\-]/g, '');
                setName(filteredValue);
                if (formError) setFormError(null);
              }}
              placeholder="e.g., Jan 2026 - Dec 2026"
              inputProps={{ maxLength: 90 }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Start Date *"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                if (formError) setFormError(null);
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="End Date *"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: startDate || today }}
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                if (formError) setFormError(null);
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={submitting} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <Save />}
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          {submitting ? 'Saving...' : 'Save Leave Period'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Leave Periods Table ──────────────────────────────────────────────────────
const LeavePeriodTable: React.FC<{
  periods: LeavePeriodDto[];
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}> = ({ periods, loading, error, onRefresh }) => {
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const [editItem, setEditItem] = useState<LeavePeriodDto | null>(null);
  const [editName, setEditName] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editIsActive, setEditIsActive] = useState(true);
  const [editSaving, setEditSaving] = useState(false);
  const [editFormError, setEditFormError] = useState<string | null>(null);

  const formatDate = (isoDate: string) => {
    const [year, month, day] = isoDate.split('T')[0].split('-');
    return `${day}-${month}-${year}`;
  };

  const convertDisplayDateToISO = (displayDate: string): string => {
    const [day, month, year] = displayDate.split('-');
    return `${year}-${month}-${day}`;
  };

  const handleToggleStatus = async (period: LeavePeriodDto) => {
    setUpdatingId(period.id);
    try {
      await leaveApi.updateLeavePeriod(period.id, {
        id: period.id,
        name: period.name,
        startDate: convertDisplayDateToISO(period.startDate),
        endDate: convertDisplayDateToISO(period.endDate),
        isActive: !period.isActive,
      });
      toast.success(
        `Leave period ${!period.isActive ? 'activated' : 'deactivated'} successfully`
      );
      onRefresh();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setUpdatingId(null);
    }
  };

  const openEditDialog = (period: LeavePeriodDto) => {
    setEditItem(period);
    setEditName(period.name);
    setEditStartDate(period.startDate.split('T')[0]);
    setEditEndDate(period.endDate.split('T')[0]);
    setEditIsActive(period.isActive);
    setEditFormError(null);
  };

  const handleEditSave = async () => {
    if (!editItem) return;

    const normalizedName = editName.trim();
    if (!normalizedName || !editStartDate || !editEndDate) {
      toast.error('Please fill all required fields');
      return;
    }

    setEditSaving(true);
    try {
      const payload: UpdateLeavePeriodPayload = {
        id: editItem.id,
        name: normalizedName,
        startDate: editStartDate,
        endDate: editEndDate,
        isActive: editIsActive,
      };
      await leaveApi.updateLeavePeriod(editItem.id, payload);
      toast.success('Leave period updated successfully');
      setEditItem(null);
      onRefresh();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to update leave period');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this leave period?')) return;
    try {
      await leaveApi.deleteLeavePeriod(id);
      toast.success('Leave period deleted');
      onRefresh();
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          'This leave period cannot be deleted because it is already used by employees.'
      );
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell><strong>Leave Period Name</strong></TableCell>
                <TableCell><strong>Start Date</strong></TableCell>
                <TableCell><strong>End Date</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No leave periods found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                periods.map((period) => (
                  <TableRow key={period.id} hover>
                    <TableCell>{period.name}</TableCell>
                    <TableCell>{formatDate(period.startDate)}</TableCell>
                    <TableCell>{formatDate(period.endDate)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch
                          checked={period.isActive}
                          onChange={() => handleToggleStatus(period)}
                          disabled={updatingId === period.id}
                          size="small"
                        />
                        {updatingId === period.id && <CircularProgress size={20} />}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEditDialog(period)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(period.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Edit Leave Period Dialog ── */}
      <Dialog
        open={!!editItem}
        onClose={() => !editSaving && setEditItem(null)}
        maxWidth="sm"
        fullWidth
        sx={{ textTransform: 'none' }}
      >
        <DialogTitle>Update Leave Period</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {editFormError && <Alert severity="error">{editFormError}</Alert>}

            <TextField
              label="Leave Period Name *"
              fullWidth
              size="small"
              value={editName}
              onChange={(e) => {
                const filteredValue = e.target.value.replace(/[^A-Za-z0-9\s\-]/g, '');
                setEditName(filteredValue);
                if (editFormError) setEditFormError(null);
              }}
            />
            <TextField
              label="Start Date *"
              type="date"
              fullWidth
              size="small"
              value={editStartDate}
              onChange={(e) => {
                setEditStartDate(e.target.value);
                if (editFormError) setEditFormError(null);
              }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date *"
              type="date"
              fullWidth
              size="small"
              value={editEndDate}
              onChange={(e) => {
                setEditEndDate(e.target.value);
                if (editFormError) setEditFormError(null);
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: editStartDate }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editIsActive}
                  onChange={(e) => setEditIsActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditItem(null)} disabled={editSaving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSave}
            disabled={editSaving}
            startIcon={editSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            sx={{ textTransform: 'none' }}
          >
            {editSaving ? 'Saving...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const LeavePeriodPage: React.FC = () => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [periods, setPeriods] = useState<LeavePeriodDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPeriods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await leaveApi.getLeavePeriods();
      setPeriods(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch leave periods');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPeriods();
  }, [fetchPeriods]);

  return (
    <Box>
      {/* ── Header row ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h4" fontWeight="bold">
          Leave Period
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => setOpenAddDialog(true)}
          sx={{ textTransform: 'none' }}
        >
          Add Leave Period
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Define leave cycles for your organization.
      </Typography>

      {/* ── Table directly on page ── */}
      <LeavePeriodTable
        periods={periods}
        loading={loading}
        error={error}
        onRefresh={fetchPeriods}
      />

      {/* ── Add dialog ── */}
      <AddLeavePeriodDialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        onSaved={fetchPeriods}
      />
    </Box>
  );
};

export default LeavePeriodPage;